import { useEffect, useId, useRef, useState } from 'react'

/**
 * Thin wrapper over the native <dialog>, driven by an `open` prop.
 *
 * showModal() is worth having rather than a hand-rolled overlay: it gives the
 * top layer, a ::backdrop to style, focus trapping and Escape-to-close for
 * free — all things a div-based modal has to reimplement badly.
 */
function Modal({
  open,
  onClose,
  labelledBy,
  children,
}: {
  open: boolean
  onClose: () => void
  labelledBy: string
  children: React.ReactNode
}): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])

  // Fires for Escape and for our own close() alike; the caller's handler is
  // idempotent, so both paths land in the same place.
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    dialog.addEventListener('close', onClose)
    return () => dialog.removeEventListener('close', onClose)
  }, [onClose])

  return (
    <dialog className="dialog" ref={ref} aria-labelledby={labelledBy}>
      {children}
    </dialog>
  )
}

/** Replaces window.confirm for destructive actions. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
}): JSX.Element {
  const headingId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Focus the safe option, not the destructive one.
  useEffect(() => {
    if (open) cancelRef.current?.focus()
  }, [open])

  return (
    <Modal open={open} onClose={onCancel} labelledBy={headingId}>
      <div className="dialog__body">
        <h2 id={headingId}>{title}</h2>
        <p className="muted">{message}</p>
        <div className="dialog__actions">
          <button type="button" className="button--ghost" ref={cancelRef} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="button--danger-solid" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/** Replaces the click-the-title-to-edit flow for renaming. */
export function RenameDialog({
  open,
  title,
  label,
  initialValue,
  onSubmit,
  onCancel,
}: {
  open: boolean
  title: string
  label: string
  initialValue: string
  onSubmit: (value: string) => void
  onCancel: () => void
}): JSX.Element {
  const headingId = useId()
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState(initialValue)

  // Reset to the current name each time it opens, and select it so typing
  // replaces rather than appends.
  useEffect(() => {
    if (!open) return
    setValue(initialValue)
    const input = inputRef.current
    if (!input) return
    input.focus()
    input.select()
  }, [open, initialValue])

  const trimmed = value.trim()

  return (
    <Modal open={open} onClose={onCancel} labelledBy={headingId}>
      <form
        className="dialog__body"
        onSubmit={(event) => {
          event.preventDefault()
          if (trimmed) onSubmit(trimmed)
        }}
      >
        <h2 id={headingId}>{title}</h2>
        <label className="dialog__label" htmlFor={inputId}>
          {label}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          maxLength={200}
        />
        <div className="dialog__actions">
          <button type="button" className="button--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={!trimmed}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}
